import React, { useEffect, useState } from "react";
import "../../styles/Courses.css";
import { getCourseCategories, getCourses } from "../../api/contentApi";
import { courseTitleToSlug } from "../../api/courseSlug";
import CustomYouTubePlayer from "../learning/CustomYouTubePlayer";

export default function Courses({ onSelectCourse }) {
  const [activeCategory, setActiveCategory] = useState("All Courses");
  const [searchQuery, setSearchQuery] = useState("");
  const [apiCourses, setApiCourses] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setLoadError("");
    getCourses({
      search: searchQuery,
      category: activeCategory === "All Courses" ? "" : activeCategory,
    })
      .then((courses) => {
        if (!cancelled) {
          setApiCourses(Array.isArray(courses) ? courses : []);
        }
      })
      .catch((error) => {
        console.error("Unable to load courses:", error);
        if (!cancelled) {
          setApiCourses([]);
          setLoadError("Unable to load courses. Please try again.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeCategory, searchQuery]);

  useEffect(() => {
    getCourseCategories()
      .then((items) => setApiCategories(Array.isArray(items) ? items : []))
      .catch((error) => console.error("Unable to load course categories:", error));
  }, []);

  const coursesForDisplay = apiCourses.map((course) => ({
    id: String(course.id),
    courseType: course.courseType,
    title: course.title,
    instructor: course.instructor || "Creator Dost Mentor",
    instructorRole: course.instructorRole || "Creator educator",
    instructorAvatar: course.instructorAvatar,
    badge: course.courseType || "Creator Program",
    category: course.category || "Content Creation",
    rating: "New",
    reviews: "",
    enrolled: "",
    duration: course.duration ? `${course.duration}h` : "Self-paced",
    price: course.totalPrice ? `₹${course.totalPrice}` : "View details",
    originalPrice: course.price ? `₹${course.price}` : "",
    thumbnail: course.imageUrl,
    videoUrl: course.videoUrl,
    highlights: String(course.details || "Practical creator skills")
      .split(/[.!?]/)
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 3),
  }));

  const categoryIcons = {
    "YouTube Growth": "▶",
    "Video Editing": "🎬",
    "Instagram Growth": "📷",
    "Content Creation": "💡",
    "Monetization": "💰",
    "Creator Business": "💼",
  };

  const categories = [
    { label: "All Courses", icon: "▦" },
    ...apiCategories.map((category) => ({
      label: category,
      icon: categoryIcons[category] || "•",
    })),
  ];

  const filteredCourses = coursesForDisplay;

  const featuredVideoUrl = "https://www.youtube.com/watch?v=aqz-KE-bpKQ";

  const handleCardClick = (course) => {
    if (onSelectCourse) {
      onSelectCourse(course);
    } else {
      const courseSlug = courseTitleToSlug(course?.title);
      window.location.hash = `#course-details?course=${encodeURIComponent(courseSlug)}`;
    }
  };

  return (
    <div className="crs-page-wrapper">
      
      {/* 1. SPOTLIGHT FLAGSHIP HERO BANNER */}
      <section className="crs-spotlight-hero">
        <div className="crs-max-width crs-spotlight-grid">
          
          <div className="crs-spotlight-left">
            <span className="crs-badge-glow">🔥 FLAGSHIP MASTERCLASS</span>
            <h1 className="crs-spotlight-title">
              Beyond School. <span className="crs-text-cyan">Build Skills.</span>
            </h1>
            <p className="crs-spotlight-desc">
              Learn content creation, algorithmic growth, and video editing directly from Graduate FF & team—the creators behind 5+ channels, 10M+ subscribers, and 2 Billion+ views.
            </p>

            <div className="crs-spotlight-meta-row">
              <span className="crs-stars-gold">★ 4.9 ★★★★★</span>
              <span className="crs-meta-divider">|</span>
              <div className="crs-enrolled-stack">
                <span className="crs-avatar-cluster">
                  <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=80&q=80" alt="Student 1" />
                  <img src="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=80&q=80" alt="Student 2" />
                  <img src="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&w=80&q=80" alt="Student 3" />
                </span>
                <strong>50,000+ Happy Learners</strong>
              </div>
            </div>

            
          </div>

          <div className="crs-spotlight-right">
            <div
              className="crs-spotlight-media-card"
              onClick={(event) => {
                if (event.target.closest(".cd-custom-player")) {
                  event.stopPropagation();
                  return;
                }

                handleCardClick("beyond-school-skills");
              }}
            >
              <CustomYouTubePlayer
                videoUrl={featuredVideoUrl}
                title="Beyond School. Build Skills."
                className="crs-spotlight-player"
                autoplay={false}
                autoPlayWhenVisible={true}
                mutedAutoplay={true}
              />
              {/* <span className="crs-spotlight-runtime-badge">⏱️ 8h 45m Complete Blueprint</span> */}
            </div>
          </div>

        </div>
      </section>

      {/* 2. DEDICATED SEARCH & CATEGORY FILTER BAR */}
      <section className="crs-controls-section">
        <div className="crs-max-width crs-controls-wrapper">
          
          {/* TOP ROW: SEARCH & STATUS */}
          <div className="crs-controls-top-row">
            <div className="crs-search-wrapper">
              <span className="crs-search-glass-icon">🔍</span>
              <input 
                type="text" 
                placeholder="Search by course topic, skills, or mentor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button 
                  className="crs-search-clear-btn" 
                  onClick={() => setSearchQuery("")}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="crs-results-counter">
              {!isLoading && <>Showing <strong>{filteredCourses.length}</strong> Courses</>}
            </div>
          </div>

          {/* BOTTOM ROW: SPACIOUS CATEGORY PILLS */}
          <div className="crs-pills-row">
            {categories.map((cat) => (
              <button
                key={cat.label}
                className={`crs-category-pill-btn ${activeCategory === cat.label ? "active" : ""}`}
                onClick={() => setActiveCategory(cat.label)}
              >
                <span className="crs-pill-icon">{cat.icon}</span>
                <span className="crs-pill-text">{cat.label}</span>
              </button>
            ))}
          </div>

        </div>
      </section>

      {/* 3. COURSES CATALOG GRID */}
      <section className="crs-max-width crs-grid-section">
        <div className="crs-section-header-row">
          <div>
            <span className="crs-section-kicker">CURATED PROGRAMS</span>
            <h2>{activeCategory} <span className="crs-counter-chip">({filteredCourses.length})</span></h2>
          </div>
        </div>

        <div className="crs-catalog-grid">
          {isLoading ? (
            <div className="crs-list-state" role="status" aria-label="Loading courses" aria-live="polite">
              <span className="crs-loader" aria-hidden="true" />
            </div>
          ) : loadError ? (
            <div className="crs-list-state" role="alert">{loadError}</div>
          ) : filteredCourses.length === 0 ? (
            <div className="crs-list-state" role="status">
              No courses found. Try changing your search or category.
            </div>
          ) : filteredCourses.map((course) => (
            <div 
              key={course.id} 
              className="crs-card-item"
              onClick={() => handleCardClick(course)}
            >
              <div className="crs-card-thumb-frame">
                <img src={course.thumbnail} alt={course.title} />
                <span className="crs-card-badge-pill">{course.badge}</span>
                <span className="crs-card-duration-tag">⏱️ {course.duration}</span>
              </div>

              <div className="crs-card-body-panel">
                <div className="crs-card-rating-line">
                  <span className="crs-stars">★ {course.rating}</span>
                  <span className="crs-review-count">({course.reviews})</span>
                  <span className="crs-dot-sep">•</span>
                  <span className="crs-enrolled-txt">{course.enrolled}</span>
                </div>

                <h3 className="crs-course-name">{course.title}</h3>
                
                <div className="crs-card-mentor-row">
                  <img src={course.instructorAvatar} alt={course.instructor} />
                  <div>
                    <h5>{course.instructor}</h5>
                    <p>{course.instructorRole}</p>
                  </div>
                </div>

                <ul className="crs-card-outcomes-list">
                  {course.highlights.slice(0, 3).map((point, idx) => (
                    <li key={idx}>✓ {point}</li>
                  ))}
                </ul>

                <div className="crs-card-bottom-footer">
                  <div className="crs-price-group">
                    <span className="crs-deal-price">{course.price}</span>
                    <del className="crs-strike-price">{course.originalPrice}</del>
                  </div>

                  <button
                    className="crs-btn-view-course"
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleCardClick(course);
                    }}
                  >
                    View Course ➔
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}